/**
 * The command envelope, and the order commands are applied in.
 *
 * These are the two things a client, a district simulation and a world server
 * have to agree on before any of them can be written. A client stamps a command,
 * a server accepts it, a settlement worker replays it a season later on a machine
 * that never saw the session — and all three have to mean the same thing by it,
 * and apply it in the same place in the sequence.
 *
 * The *buffer* that holds pending commands is not one of those things. It is an
 * implementation, it lives in `@antification/core` as `CommandQueue`, and a
 * server is free to hold its pending commands in a database instead. What it may
 * not do is invent its own tie-break: that is here, and it is the same everywhere.
 */

/**
 * One instruction to the simulation.
 *
 * `args` must be canonical JSON — no floats that came from a wall clock, no
 * iteration-order-dependent objects. Whatever is in here is replayed verbatim
 * when a season is re-simulated, on a machine that no longer has the session
 * that produced it.
 */
export interface Command {
  /** The tick at whose head this is applied. Assigned by the scheduler, fixed by the server. */
  tick: number;
  /** Who issued it. Also the first tie-break, so two actors never race. */
  actor: string;
  /** Monotonic per actor. The second tie-break, so one actor's own commands keep their order. */
  seq: number;
  /** The verb. See the design document §18 for the mayor and governor vocabularies. */
  verb: string;
  /** Verb arguments. Serialisable, and part of the replay record. */
  args?: Record<string, unknown>;
}

/**
 * Total order over commands.
 *
 * Two peers receive the same commands in different orders — that is what a
 * network is. They must nonetheless apply them in the same order, and it has to
 * be an order derivable from the commands themselves rather than from arrival.
 *
 * `(tick, actor, seq)` is that order. Actor is compared as a string so the
 * result does not depend on a join order or on any registry the peers might
 * disagree about.
 */
export function compareCommands(a: Command, b: Command): number {
  if (a.tick !== b.tick) return a.tick - b.tick;
  if (a.actor !== b.actor) return a.actor < b.actor ? -1 : 1;
  return a.seq - b.seq;
}

/** Why a command could not be scheduled where it asked to be. */
export type ScheduleRejection =
  /** Its tick has already been simulated. Only the issuer's clock was wrong. */
  | { ok: false; reason: 'late'; earliestTick: number }
  /** An identical (actor, seq) is already queued. Retransmission, not a new command. */
  | { ok: false; reason: 'duplicate'; existing: Command }
  /**
   * The verb is unknown, or its arguments are not something the simulation can
   * apply.
   *
   * No queue ever produces it — a queue carries verbs, it does not read them.
   * The rejection lives beside the other two because it belongs to the same
   * result type, and because the rule it enforces is queue-shaped: malformed
   * input must be refused at the door rather than stored, since anything that
   * gets in is replayed for the rest of the world's life.
   */
  | { ok: false; reason: 'invalid'; detail: string };

export type ScheduleResult = { ok: true; command: Command } | ScheduleRejection;

/**
 * A queue's complete state, for snapshots.
 *
 * A document rather than an implementation: a world holding a `PAVE` for tick
 * 4,100 is not the world that is not holding one, so this travels with the
 * district.
 */
export interface CommandQueueState {
  lastAppliedTick: number;
  pending: Command[];
}
